import {SettingParam, SettingParamTypes} from "@/types/node";
import {useEffect, useState, useCallback, memo} from "react";
import {Button} from "../ui/Button";
import {Plus, Trash} from "lucide-react";
import {Popover, PopoverContent, PopoverTrigger} from "../ui/Popover";
import {SettingInput} from "./SettingInput";
import {SettingTextarea} from "./SettingTextarea";
import {SettingSelect} from "./SettingSelect";
import {Icon} from "../ui/icon";
import {CaptureVariableIcon} from "../icons/CaptureVariableIcon";
import {Variable, useVariables} from "@/contexts/VariablesContext";
import {useSubflows} from "@/contexts/SubflowsContext";
import {OperatorOptions, VariableType} from "./constants";
import {cn, sanitizeVariableName} from "@/lib/utils";
import {MultiTagInput} from "../ui/MultiTagInput";

export const VariableTypeOptions = OperatorOptions.map(type => ({
  name: type.name,
  value: type.value,
}));

const PopoverContentContainer = memo(
  ({
    value,
    selectedNodeId,
    disabled = false,
    setOpen,
    isEditing,
    handleVariableUpdate,
    handleVariableAdd,
  }: {
    value: Variable;
    selectedNodeId: string;
    disabled: boolean;
    setOpen: (open: boolean) => void;
    isEditing: boolean;
    handleVariableUpdate: (value: Variable) => void;
    handleVariableAdd: (value: Variable) => void;
  }) => {
    const [variable, setVariable] = useState<Variable>(value);
    const [nameTouched, setNameTouched] = useState(false);

    useEffect(() => {
      setVariable(value);
      setNameTouched(false);
    }, [value, selectedNodeId]);

    let nameError: string | undefined;
    if (!variable?.name) {
      nameError = nameTouched ? "Name is required" : undefined;
    } else if (!/^[a-zA-Z0-9_]+$/.test(variable.name)) {
      nameError = "Only letters, digits, and underscores";
    }
    const isFormInvalid =
      !variable?.name ||
      !variable?.type ||
      !variable?.instructions ||
      (variable?.type === VariableType.ENUM &&
        (!variable?.enumOptions || variable.enumOptions.length === 0)) ||
      !!nameError;

    return (
      <PopoverContent side="bottom" sideOffset={8} className="w-98">
        <div className="flex flex-col">
          <div className="flex flex-row gap-4">
            <div className="flex-grow min-w-0">
              <SettingInput
                param={{
                  label: "Name",
                  type: SettingParamTypes.INPUT,
                  placeholder: "variable_name",
                  key: "name",
                  value: variable?.name,
                }}
                handleChange={(_param, value) => {
                  const sanitized = sanitizeVariableName(value);
                  setVariable({...variable, name: sanitized});
                }}
                selectedNodeId={selectedNodeId}
                disabled={disabled}
                error={nameError}
                onBlur={() => {
                  setNameTouched(true);
                }}
              />
            </div>
            <div className="">
              <SettingSelect
                param={{
                  label: "Type",
                  type: SettingParamTypes.SELECT,
                  key: "type",
                  value: variable?.type,
                }}
                handleChange={(_param, value) => {
                  setVariable({
                    ...variable,
                    type: value as VariableType,
                  });
                  setOpen(true);
                }}
                placeholder="Select Type"
                disabled={disabled}
                selectOptions={VariableTypeOptions}
                selectedNodeId={selectedNodeId}
              />
            </div>
          </div>
          <div className="flex flex-col gap-4">
            {variable?.type === VariableType.ENUM && (
              <MultiTagInput
                value={variable?.enumOptions || []}
                onChange={options =>
                  setVariable({...variable, enumOptions: options})
                }
                placeholder="Add list item"
                label="List Items"
              />
            )}
            <div className={variable?.type === VariableType.ENUM ? "mb-0" : ""}>
              <div
                className={
                  variable?.type === VariableType.ENUM ? "[&>div]:mb-0" : ""
                }
              >
                <SettingTextarea
                  param={{
                    label: "Question to ask to collect variable",
                    type: SettingParamTypes.TEXTAREA,
                    placeholder:
                      "Enter instructions on how to collect the variable.",
                    key: "instructions",
                    value: variable?.instructions,
                  }}
                  handleChange={(_param, value) =>
                    setVariable({...variable, instructions: value})
                  }
                  selectedNodeId={selectedNodeId}
                  disabled={disabled}
                  tooltipMessage={`Here you should write the exact question your agent should ask to collect this variable. For example: ‘Are you over 18 years old?’ Make sure to clarify why it’s important and ensure the answer matches official records (e.g., what is on their driver’s license).`}
                />
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              className="w-full"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              onClick={() => {
                if (isEditing) {
                  handleVariableUpdate(variable);
                } else {
                  handleVariableAdd(variable);
                }

                setNameTouched(false);
              }}
              disabled={isFormInvalid || disabled}
            >
              {isEditing ? "Update" : "Save"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    );
  },
);
PopoverContentContainer.displayName = "PopoverContentContainer";

const VariableItem = memo(
  ({
    variable,
    index,
    setPopoverTopPosition,
    onVariableClick,
    onVariableDelete,
    disabled,
  }: {
    variable: Variable;
    index: number;
    totalCount: number;
    currentIndex: number | undefined;
    open: boolean;
    setOpen: (open: boolean) => void;
    setPopoverTopPosition: (pos: number) => void;
    onVariableClick: (
      variable: Variable | null,
      index: number | undefined,
    ) => void;
    onVariableDelete: (variable: Variable) => void;
    disabled: boolean;
    selectedNodeId: string;
    currentValue: Variable;
    handleVariableUpdate: (value: Variable) => void;
    handleVariableAdd: (value: Variable) => void;
    isEditing: boolean;
  }) => {
    const variableTypeName = OperatorOptions.find(
      option => option.value === variable.type,
    );

    return (
      <div
        key={index}
        ref={ref => {
          if (ref) setPopoverTopPosition(ref.offsetTop);
        }}
        onClick={() => {
          onVariableClick(variable, index);
        }}
        className="border rounded-lg p-2.5 flex flex-col gap-2.5 cursor-pointer"
      >
        <div className="flex flex-row justify-between items-center gap-2.5">
          <div>
            <Icon className="w-11 h-11 bg-slate">
              <CaptureVariableIcon className="w-6 h-6 text-secondary" />
            </Icon>
          </div>
          <div className="flex flex-col overflow-hidden whitespace-nowrap items-start">
            <p className="text-sm text-primary font-medium overflow-hidden whitespace-nowrap text-ellipsis max-w-full">
              {variable.name}
            </p>
            <div className="flex flex-row gap-1 text-secondary font-normal text-sm overflow-hidden whitespace-nowrap text-ellipsis max-w-full">
              {variable.type && (
                <div className="capitalize">{variableTypeName.name}</div>
              )}
              {variable.type === VariableType.ENUM &&
                (variable.description || variable.instructions) && (
                  <div className="relative pl-3 before:content-['•'] before:absolute before:left-0 before:text-secondary before:text-lg before:font-bold before:!leading-5 overflow-hidden whitespace-nowrap text-ellipsis">
                    {variable.description
                      ? variable.description
                      : variable.instructions}
                  </div>
                )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            className="[&_svg]:size-5 ml-auto"
            disabled={disabled}
            asDiv
            onClick={e => {
              e.stopPropagation();
              onVariableDelete(variable);
            }}
          >
            <Trash className="text-secondary" />
          </Button>
        </div>
      </div>
    );
  },
);
VariableItem.displayName = "VariableItem";

export const SettingCapture = ({
  param,
  handleChange,
  selectedNodeId,
  disabled = false,
}: {
  param: SettingParam & {value: Variable[]};
  handleChange: (param: SettingParam, value: Variable[]) => void;
  selectedNodeId: string;
  disabled: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState<Variable>();
  const [currentIndex, setCurrentIndex] = useState<number>();
  const [values, setValues] = useState<Variable[]>(param.value || []);
  const [, setPopoverTopPosition] = useState<number>(0);
  const [formKey, setFormKey] = useState(0);
  const {addVariable, removeVariable, updateVariable} = useVariables();
  const {selectedSubflowId, selectedSubflow} = useSubflows();
  const currentSubflowId = selectedSubflowId || "main";
  const currentSubflowName = selectedSubflow?.name || "Main";

  useEffect(() => {
    setValues(param.value || []);
  }, [param, selectedNodeId]);

  const handleVariableAdd = useCallback(
    (value: Variable) => {
      setValues(prev => {
        const newValues = [...prev, value];
        handleChange(param, newValues);
        return newValues;
      });
      setOpen(false);
      setCurrentIndex(undefined);
      addVariable({
        name: value.name,
        type: value.type as VariableType,
        instructions: value.instructions,
        required: value.required,
        isValid: true,
        id: "",
        source: "",
        subflowId: currentSubflowId,
        subflowName: currentSubflowName,
      });
    },
    [param, handleChange, addVariable, currentSubflowId, currentSubflowName],
  );

  const handleVariableUpdate = useCallback(
    (value: Variable) => {
      setValues(prev => {
        const newValues = prev.map((v, i) => (i === currentIndex ? value : v));
        handleChange(param, newValues);
        return newValues;
      });
      setOpen(false);
      setCurrentIndex(undefined);
      updateVariable(value.name, {
        type: value.type as VariableType,
        instructions: value.instructions,
        required: value.required,
        isValid: true,
        id: "",
        source: "",
        subflowId: currentSubflowId,
        subflowName: currentSubflowName,
      });
    },
    [
      currentIndex,
      param,
      handleChange,
      updateVariable,
      currentSubflowId,
      currentSubflowName,
    ],
  );

  const handleVariableDelete = useCallback(
    (value: Variable) => {
      setValues(prev => {
        const newValues = prev.filter(v => v.name !== value.name);
        handleChange(param, newValues);
        return newValues;
      });
      setOpen(false);
      setCurrentIndex(undefined);
      removeVariable(value.name);
    },
    [param, handleChange, removeVariable],
  );

  const handleVariableClick = useCallback(
    (variable: Variable | null, index: number | undefined) => {
      if (variable) {
        setCurrentValue({...variable});
        setCurrentIndex(index);
        setIsEditing(true);
        setFormKey(prev => prev + 1);
        setOpen(true);
      } else {
        setCurrentIndex(undefined);
      }
    },
    [],
  );

  return (
    <div>
      <Popover
        open={open}
        setOpen={isOpen => {
          setOpen(isOpen);
          if (!isOpen) setCurrentIndex(undefined);
        }}
      >
        <PopoverTrigger
          className={cn("w-full", disabled && "opacity-50 pointer-events-none")}
        >
          <Button
            variant="outline"
            className="w-full px-3 gap-1.5"
            asDiv
            disabled={disabled}
            onClick={() => {
              setIsEditing(false);
              setCurrentValue({
                name: "",
                type: VariableType.STRING,
                instructions: "",
                required: true,
                id: "",
              });
              setCurrentIndex(undefined);
              setFormKey(prev => prev + 1);
            }}
          >
            <Plus className="text-primary-accent w-4 h-4" />
            Add Variable
          </Button>
        </PopoverTrigger>
        <PopoverContentContainer
          key={formKey}
          value={currentValue}
          selectedNodeId={selectedNodeId}
          disabled={disabled}
          setOpen={setOpen}
          isEditing={isEditing}
          handleVariableUpdate={handleVariableUpdate}
          handleVariableAdd={handleVariableAdd}
        />
      </Popover>
      <div className="flex flex-col gap-3 mt-4">
        {[...values].map((variable, index) => (
          <VariableItem
            key={index}
            variable={variable}
            index={index}
            totalCount={values.length}
            currentIndex={currentIndex}
            open={open}
            setOpen={setOpen}
            setPopoverTopPosition={setPopoverTopPosition}
            onVariableClick={handleVariableClick}
            onVariableDelete={handleVariableDelete}
            disabled={disabled}
            selectedNodeId={selectedNodeId}
            currentValue={currentValue}
            handleVariableUpdate={handleVariableUpdate}
            handleVariableAdd={handleVariableAdd}
            isEditing={isEditing}
          />
        ))}
      </div>
    </div>
  );
};
